/** The coach's weekly summary (DESIGN §68.3): on the Week's Overview, and beside the Plan for the week before. */
import "./Summary.css";
import { useContext, useId, useState } from "react";
import { Link } from "react-router";
import { PortalContext } from "../context.js";
import { errorMessage, loadSummary, summaryKey, useResource, writeSummary, type SummaryItem } from "../data.js";
import { Icon } from "../../builder/components/ui.js";
import { mondayOf, todayString, weekRange } from "../model/days.js";
import { href } from "../paths.js";

/** `Sun 5:00 PM`; a date in place of the weekday once it is more than six days ago. */
export function writtenText(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const time = at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).replace(/\s/g, " ");
  const recent = now.getTime() - at.getTime() < 6 * 86_400_000;
  return recent ? at.toLocaleDateString("en-US", { weekday: "short" }) + " " + time : at.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + ", " + time;
}

export const summaryIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="bp-icon">
    <g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5.5 2.5h6l3.5 3.5v10.5a1 1 0 0 1-1 1H5.5a1 1 0 0 1-1-1v-13a1 1 0 0 1 1-1Z" />
      <path d="M7.5 9.5h5M7.5 12.5h5M7.5 15h3" />
    </g>
  </svg>
);

function List({ title, items, week }: { title: string; items: SummaryItem[]; week: string }) {
  const id = useId();
  if (!items.length) return null;
  return (
    <section className="sum-group" aria-labelledby={id}>
      <h3 id={id}>{title}</h3>
      <ul className="sum-list">
        {items.map((item, i) => (
          <li key={item.athleteID + ":" + i} className="sum-row">
            <Link className="sum-name" to={href(week, "athlete", item.athleteID)}>
              {item.name}
            </Link>
            <span className="sum-text">{item.text}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The summary of the week starting `week`. No card for a week still to come, while it loads, when it
 * couldn't be read, or when there is none and `logged` says nothing was logged. `heading` false when
 * the place it shows already names it.
 */
export function SummaryCard({ week, logged, heading = true }: { week: string; logged?: boolean; heading?: boolean }) {
  const announce = useContext(PortalContext)?.announce;
  const future = week > mondayOf(todayString());
  const read = useResource(future ? null : summaryKey(week), () => loadSummary(week));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ week: string; message: string } | null>(null);
  const headingID = useId();
  if (future || read.value === undefined) return null;
  const summary = read.value;
  if (!summary && logged === false) return null;

  const write = () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    writeSummary(week).then(
      () => {
        setBusy(false);
        announce?.("Summary written");
      },
      (err: unknown) => {
        setBusy(false);
        setError({ week, message: errorMessage(err) });
      },
    );
  };

  const shownError = error?.week === week ? error.message : null;
  const written = summary ? writtenText(summary.writtenAt) : "";
  return (
    <section className="sum" aria-labelledby={heading ? headingID : undefined} aria-label={heading ? undefined : "Summary"}>
      {heading ? <h2 id={headingID}>Summary</h2> : null}
      <div className="sum-card" aria-busy={busy}>
        {summary ? (
          <>
            {summary.overview ? <p className="sum-overview">{summary.overview}</p> : null}
            <List title="Hold back" items={summary.holdBack} week={week} />
            <List title="Push on" items={summary.pushOn} week={week} />
            <List title="Catch up" items={summary.catchUp} week={week} />
            <p className="sum-foot">
              {written ? <span className="num">Written {written}</span> : null}
              {written ? <span aria-hidden="true">·</span> : null}
              <button type="button" className="sum-link" onClick={write}>
                {busy ? "Writing…" : "Write Again"}
              </button>
            </p>
          </>
        ) : (
          <div className="sum-empty">
            <p>No summary for this week yet.</p>
            <button type="button" className="btn primary sum-write" onClick={write}>
              {busy ? "Writing…" : "Write Summary"}
            </button>
          </div>
        )}
        {shownError ? (
          <p className="sum-error" role="alert">
            {shownError}
          </p>
        ) : null}
      </div>
    </section>
  );
}

/** The Plan's side panel (§68.3): the summary of the week before the one being planned. */
export function LastWeekPanel({ week, onClose }: { week: string; onClose: () => void }) {
  const id = useId();
  return (
    <aside className="bp-side-panel glass" aria-labelledby={id}>
      <div className="bp-summary-panel">
        <div className="bp-panel-head">
          <div>
            <h2 id={id}>Last week</h2>
            <p className="num">{weekRange(week)}</p>
          </div>
          <button type="button" className="bp-icon-btn" aria-label="Close last week" title="Close" onClick={onClose}>
            {Icon.close}
          </button>
        </div>
        <SummaryCard week={week} heading={false} />
      </div>
    </aside>
  );
}
